import * as Sentry from '@sentry/browser';
import {
  Product,
  SurveyUpdatedBy,
  WebAnalyticsEvent,
  WebAnalyticsEventName,
} from 'hydra-data-types';
import { getTimezoneOffsetInHours } from 'ts-utils';
import { Gender } from '../businessLogic/AssessmentQuestionTypes';
import { appVariant } from './AppUtils';
import { ConsentManager, consentCategoryPreferences, hasGlobalPrivacyControl } from './Consent';
import { Environment } from './Environment';
import { attachFacebookValueEventProperties } from './FacebookValueEvents';

const WRITE_KEY = Environment.DEVELOPMENT
  ? appVariant({
      // https://app.segment.com/mindset-health/sources/meno_web_assessment_dev/settings/keys
      meno: 'q9D09sR9osvFah2UtoA8dBXiLxBf6n6L',
      // https://app.segment.com/mindset-health/sources/nerva_web_assessment_dev/settings/keys
      nerva: 'nNKg3unPOtPmHDptfyEsR3nRu6sAV2iB',
      // https://app.segment.com/mindset-health/sources/quit_web_assessment_dev/settings/keys
      quit: 'Z6CXMWKqUGhwiMBfpiA03CY7zwXWcyH9',
      // https://app.segment.com/mindset-health/sources/pain_web_assessment_dev/settings/keys
      pain: 'sWDeFhFBBtfR8dbc3KGGQDEmeg2GBgid',
    })
  : appVariant({
      // https://app.segment.com/mindset-health/sources/meno_web_assessment/settings/keys
      meno: 'pn8gDP8i4FvhaVp9TqP3qx5zJLUG9W6o',
      // https://app.segment.com/mindset-health/sources/nerva_web_assessment/settings/keys
      nerva: 'j6OUxGgL5WMthnTbLFFY6Pxaiho6h8vd',
      // https://app.segment.com/mindset-health/sources/quit_web_assessment/settings/keys
      quit: 'CthXc5IoChb49ARMkyRtmvdTe6Io7jPs',
      // https://app.segment.com/mindset-health/sources/pain_web_assessment/settings/keys
      pain: 'goqPyBixXNvu2xOqjPP1lMMKLMpZxhU1',
    });

let initialized = false;

/** Initialize Segment after consent has resolved. */
function initialize(): void {
  if (initialized) return;
  initialized = true;
  window.analytics.addSourceMiddleware(addConsentContextMiddleware);
  window.analytics.addSourceMiddleware(addProductPropertiesMiddleware);
  // Since much of the flow is before signup (where we can call `identify`) we attach available user traits as context
  window.analytics.addSourceMiddleware(addUserTraitsContextMiddleware);
  window.analytics.addSourceMiddleware(logEventsMiddleware);
  window.analytics.load(WRITE_KEY);
  window.analytics.group(Environment.APP);
}

/**
 * Link users and their actions, to a userId and traits in **Segment**, **Sentry**
 * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
 *
 * Optionally add a `contextEvent` to describe the identify call (e.g. `a-signup-succeeded`) which is useful
 * during testing to recognize the identify call.
 */
function identify(traits: TrackingTraits, contextualEvent?: WebAnalyticsEventName): void {
  if (traits.userId !== undefined) {
    Sentry.configureScope((scope) => {
      scope.setUser({ id: traits.userId });
    });
  }
  initialize();

  const userProperties = segmentTraits(traits);
  const context = { event: contextualEvent };
  const options = 'phone' in traits ? integrationFiltersForPhoneData() : undefined;

  if (traits.userId !== undefined) {
    window.analytics.identify(traits.userId, userProperties, {
      ...options,
      context,
    });
  } else {
    window.analytics.identify(userProperties, { ...options, context });
  }
}

const eventsWithPHIData = [WebAnalyticsEventName.o_question_answered];

// Customer.io is whitelisted for these PHI events to support lifecycle messaging
const phiEventsToCIO = [WebAnalyticsEventName.o_question_answered];

/**
 * Record an action a user has performed.
 * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
 */
function track(analyticsEvent: WebAnalyticsEvent): void {
  initialize();
  const { event: eventName, ...properties } = analyticsEvent;
  const isPHIEvent = eventsWithPHIData.includes(eventName);
  const options = isPHIEvent
    ? integrationFiltersForHealthData(phiEventsToCIO.includes(eventName))
    : undefined;
  window.analytics.track(
    eventName,
    attachFacebookValueEventProperties(eventName, properties),
    options
  );
}

function screen(name: string): void {
  initialize();
  // Segment specific method for tracking page views.
  // see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
  window.analytics.page(name);
}

/** @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/middleware/ */
type SourceMiddlewareOptions = {
  payload: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  next: (payload: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  integrations: string;
};

export function addConsentContextMiddleware({ payload, next }: SourceMiddlewareOptions): void {
  const consent = ConsentManager.get();
  if (consent.chosen || hasGlobalPrivacyControl()) {
    payload.obj.context = payload.obj.context ?? {};
    payload.obj.context.consent = {
      categoryPreferences: consentCategoryPreferences(consent),
    };
  }
  next(payload);
}

function addProductPropertiesMiddleware({ payload, next }: SourceMiddlewareOptions): void {
  payload.obj.properties = {
    ...payload.obj.properties,
    product: Environment.APP,
    version: Environment.VERSION,
    origin: Product.WebOnboarding,
  };
  next(payload);
}

async function addUserTraitsContextMiddleware({
  payload,
  next,
}: SourceMiddlewareOptions): Promise<void> {
  // importing with a dynamic import to prevent a circular import
  const { store } = await import('../store/Store');

  const { user, userData } = store.getState();
  payload.obj.context.traits = {
    ...payload.obj.context.traits,
    ...segmentTraits({
      email: user?.email ?? payload.obj.properties.email,
      name: user?.name ?? payload.obj.properties.name,
      lastName:
        'lastName' in user && user.lastName !== undefined
          ? user.lastName
          : payload.obj.properties.lastName,
      gender: userData.gender,
      age: userData.age,
    }),
  };
  next(payload);
}

/**
 * Explicitly set the destinations to pass specific event data.
 * This is to prevent private health/user data from being passed on to 3rd parties unnecessarily.
 *
 * Done via code because it's not currently possible via the Segment web app to block data to all destinations
 * by default.
 * See https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#managing-data-flow-with-the-integrations-object
 */
function integrationFiltersForHealthData(
  includeCustomerIO = false
): SegmentAnalytics.SegmentOpts['integrations'] {
  return {
    integrations: {
      All: false,
      Amplitude: true,
      ...(includeCustomerIO && { 'Customer.io Actions': true }),
    },
  };
}

/**
 * Phone number is to be passed only to Vero and Customer.io to be able to send SMSes to users.
 * Not to be forwarded to any other 3rd parties.
 */
function integrationFiltersForPhoneData(): SegmentAnalytics.SegmentOpts['integrations'] {
  return {
    integrations: {
      All: false,
      Vero: true,
      'Customer.io Actions': true,
    },
  };
}

function logEventsMiddleware({ payload, next }: SourceMiddlewareOptions): void {
  if (Environment.DEVELOPMENT) {
    if (payload.obj.type === 'track') {
      console.info(
        '[Analytics Event]',
        payload.obj.event,
        payload.obj.properties,
        payload.obj.context
      );
    } else if (payload.obj.type === 'identify') {
      console.info('[Analytics Identify]', payload.obj.context.event ?? '', payload.obj.traits);
    } else if (payload.obj.type === 'page') {
      console.info('[Analytics Screen Viewed]', payload.obj.name, payload.obj.properties);
    }
  }
  next(payload);
}

/** Traits as used internally by our app */
export type TrackingTraits = {
  userId?: string;
  email?: string;
  /** A user's **first name**
   *
   * This will be sent to Segment as `name` and `firstName` traits.
   * This is now also synced with Vero emails using this `name` field to refer to users.
   *
   * **Warning**: Since Segment uses `name` to refer to a users **full name** and will set it automatically, if
   * `lastName` is supplied but not `name`, Segment will automatically set `name` to be the `lastName`.
   * https://segment.com/docs/connections/spec/identify/#traits
   */
  name?: string;
  lastName?: string;
  gender?: string[];
  age?: string[];
  /** See https://help.getvero.com/cloud/articles/what-are-customer-properties/ for attribute names in vero */
  phone?: string;
  /** defines which flow config has been shown to user */
  configVersion?: string;
  experimentVariants?: Record<string, string>;
};

/**
 * Traits expected by Segment.
 * @see https://segment.com/docs/connections/spec/identify/#traits
 */
export type SegmentTraits = Omit<TrackingTraits, 'age' | 'gender' | 'experimentVariants'> & {
  id?: string;
  gender?: FacebookGenderTrait;
  age?: number;
  from?: SurveyUpdatedBy;
  phone?: string;
  timezone: number;
  configVersion?: string;
};

export type FacebookGenderTrait = 'm' | 'f';
const GENDER_TRAIT_TABLE: {
  [Property in Gender]: FacebookGenderTrait | undefined;
} = {
  [Gender.Male]: 'm',
  [Gender.Female]: 'f',
  [Gender.Nonbinary]: undefined,
  [Gender.Other]: undefined,
  [Gender.Unstated]: undefined,
};

/**
 * Converts user traits into the traits structure expected by Segment.
 * `undefined` values returned will end up being removed by JSON.stringify()
 *
 * @see https://segment.com/docs/connections/spec/identify/#traits
 */
export function segmentTraits(traits: TrackingTraits): SegmentTraits {
  // While the UI is a single select, internally our `gender` and `age` representation is a multiselect
  // of strings.
  // For `gender` facebook expects 'm'/'f' only.
  const gender =
    traits.gender !== undefined && traits.gender.length > 0
      ? GENDER_TRAIT_TABLE[traits.gender[0] as Gender]
      : undefined;

  const age =
    traits.age !== undefined && traits.age.length > 0 ? parseInt(traits.age[0]) : undefined;

  function filterEmptyString(val: string | undefined) {
    return val === '' ? undefined : val;
  }

  return {
    id: filterEmptyString(traits.userId),
    // Although segment expects `id` we have been sending `userId` so we will continue to do so
    userId: filterEmptyString(traits.userId),
    email: filterEmptyString(traits.email),
    name: filterEmptyString(traits.name),
    // not including `firstName` as we use `name` to represent the firstName.
    lastName: filterEmptyString(traits.lastName),
    gender,
    age,
    phone: filterEmptyString(traits.phone),
    from: SurveyUpdatedBy.Web,
    // timezone is specifically needed for Vero based on https://help.getvero.com/articles/scheduling-timing
    // this helps Vero schedule communications correctly based on client's timezone.
    timezone: getTimezoneOffsetInHours(),
    configVersion: traits.configVersion,
    ...traits.experimentVariants,
  };
}

export const Analytics = {
  initialize,
  identify,
  track,
  screen,
  Event: WebAnalyticsEventName,
};
